import { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, Activity, ShoppingCart, Percent, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { adminGetAnalyticsOverview, adminGetSymptomAnalytics } from '@/lib/api';
import { toast } from 'sonner';

const AdminAnalytics = () => {
  const [overview, setOverview] = useState(null);
  const [symptomData, setSymptomData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const [overviewData, symptoms] = await Promise.all([
        adminGetAnalyticsOverview(),
        adminGetSymptomAnalytics()
      ]);
      setOverview(overviewData);
      setSymptomData(symptoms);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const getMaxCount = (items) => {
    if (!items || items.length === 0) return 1;
    return Math.max(...items.map(i => i.count));
  };

  if (loading) {
    return <div className="text-stone-400 p-8">Loading analytics...</div>;
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="font-serif text-2xl text-[#2C2C2C] mb-1">Analytics</h1>
        <p className="text-stone-600 text-sm">Quiz performance, recommendations, and conversion insights</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="metric-quiz-starts">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#4A6763]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#2C2C2C]">
                  {overview?.total_quiz_starts || 0}
                </p>
                <p className="text-xs text-stone-500">Quiz Starts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="metric-quiz-completions">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-green-600" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#2C2C2C]">
                  {overview?.total_quiz_completions || 0}
                </p>
                <p className="text-xs text-stone-500">Completions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="metric-completion-rate">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#C7A07A]/10 flex items-center justify-center">
                <Percent className="w-5 h-5 text-[#C7A07A]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#2C2C2C]">
                  {overview?.completion_rate || 0}%
                </p>
                <p className="text-xs text-stone-500">Completion Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="metric-cart-adds">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#2C2C2C]">
                  {overview?.total_cart_adds || 0}
                </p>
                <p className="text-xs text-stone-500">Cart Adds</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Conditions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg font-serif">
              <TrendingUp className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
              Most Common Quiz Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            {overview?.top_conditions?.length > 0 ? (
              <div className="space-y-4">
                {overview.top_conditions.map((item, index) => {
                  const maxCount = getMaxCount(overview.top_conditions);
                  const percentage = (item.count / maxCount) * 100;
                  return (
                    <div key={item.id || index} data-testid={`condition-stat-${index}`}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-stone-700">{item.name}</span>
                        <span className="text-sm text-stone-500">{item.count} matches</span>
                      </div>
                      <Progress value={percentage} className="h-2" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-stone-500 text-center py-8">No data yet. Complete some quizzes to see trends.</p>
            )}
          </CardContent>
        </Card>

        {/* Top Protocols */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg font-serif">
              <BarChart3 className="w-5 h-5 text-[#C7A07A]" strokeWidth={1.5} />
              Most Recommended Protocols
            </CardTitle>
          </CardHeader>
          <CardContent>
            {overview?.top_protocols?.length > 0 ? (
              <div className="space-y-4">
                {overview.top_protocols.map((item, index) => {
                  const maxCount = getMaxCount(overview.top_protocols);
                  const percentage = (item.count / maxCount) * 100;
                  return (
                    <div key={item.id || index} data-testid={`protocol-stat-${index}`}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-stone-700">{item.name}</span>
                        <span className="text-sm text-stone-500">{item.count} times</span>
                      </div>
                      <Progress value={percentage} className="h-2 [&>div]:bg-[#C7A07A]" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-stone-500 text-center py-8">No data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Most Added-to-Cart Products */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg font-serif">
              <ShoppingCart className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
              Most Added-to-Cart Products
            </CardTitle>
          </CardHeader>
          <CardContent>
            {overview?.top_cart_products?.length > 0 ? (
              <div className="space-y-3">
                {overview.top_cart_products.map((item, index) => (
                  <div
                    key={item.product_id || index}
                    className="flex items-center justify-between p-3 bg-stone-50 rounded-lg"
                    data-testid={`cart-product-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">
                        {index + 1}
                      </span>
                      <span className="text-sm text-stone-700">{item.product_name}</span>
                    </div>
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                      {item.count} add{item.count !== 1 ? 's' : ''}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-stone-500 text-center py-8">No cart data yet. Products added to cart from quiz results will appear here.</p>
            )}
          </CardContent>
        </Card>

        {/* Source Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg font-serif">
              <Globe className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
              Quiz Source Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {overview?.source_breakdown && Object.keys(overview.source_breakdown).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(overview.source_breakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([source, count], index) => {
                    const total = Object.values(overview.source_breakdown).reduce((a, b) => a + b, 0);
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    return (
                      <div key={source} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg" data-testid={`source-${source}`}>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-stone-700 capitalize">{source.replace(/-/g, ' ')}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-stone-500">{pct}%</span>
                          <span className="text-xs bg-[#4A6763]/10 text-[#4A6763] px-2 py-1 rounded-full">
                            {count}
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="text-stone-500 text-center py-8">No source data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Symptom Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-serif">
            <Activity className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
            Most Common Symptoms & Concerns
          </CardTitle>
        </CardHeader>
        <CardContent>
          {symptomData?.symptom_frequency?.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {symptomData.symptom_frequency.slice(0, 15).map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-stone-50 rounded-lg"
                >
                  <span className="text-sm text-stone-700">{item.symptom}</span>
                  <span className="text-xs bg-[#4A6763]/10 text-[#4A6763] px-2 py-1 rounded-full">
                    {item.count}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-stone-500 text-center py-8">No symptom data yet. Complete some quizzes to see trends.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAnalytics;
